// ThreadView — renders a conversation's messages as iMessage-style bubbles.
//
// Per session-7 rewrite:
//   - MessageBubble owns individual bubble rendering (clean email body, show-
//     original toggle, attachments, tone chip).
//   - ThreadView handles GROUPING: consecutive messages from the same sender
//     within 60 seconds suppress the avatar+name on the later bubbles so the
//     conversation visually coalesces.
//   - TIME DIVIDERS render between messages separated by >10 minutes.
//   - We no longer render the subject/channel header here; that lives in the
//     parent Center page's work-head. ThreadView is purely the message list.
//   - Auto-scroll unchanged from prior version: jump on mount/convo switch,
//     smooth when a new message arrives live.
//
// Polish #12 (collapsible thread):
//   - By default, all messages EXCEPT the last one render as CollapsedMessageRow
//     single-line rows (avatar + name + time + 60-char snippet).
//   - A "Show N earlier messages" button at the top expands every earlier
//     message into full bubbles at once.
//   - Clicking an individual collapsed row expands just that message.
//   - Threads with 1 message stay untouched. Switching conversations resets
//     expansion state.
//
// Tones are passed in from the parent (ThreadView doesn't fetch them itself)
// so the fetcher can fire once for the whole thread on Command Center load.

import { useEffect, useRef, useState } from 'react';
import type { MessageRow } from '@/lib/hooks/useConversation';
import { MessageBubble } from './MessageBubble';
import { CollapsedMessageRow } from './CollapsedMessageRow';
import type { MessageTone } from './ToneChip';
import type { Attachment } from './AttachmentGrid';

interface ThreadViewProps {
  messages: MessageRow[];
  tones?: Record<string, MessageTone>;
  /** Map of message_id to the attachments Haiku attached for that message. */
  attachmentsByMessageId?: Record<string, Attachment[]>;
  /** Polish #18 — wired to useMarkProof. Only surfaced on outbound bubbles. */
  onMarkProof?: (attachmentId: string) => void;
  /** Set of attachment ids already tracked in the proof panel. */
  proofAttachmentIds?: Set<string>;
  /** Render the mark-proof buttons in their pending/disabled state. */
  markProofPending?: boolean;
}

/** Pull initials from a display name (or fallback to first char of email). */
function initialsFrom(name: string | null, identifier: string): string {
  const source = name && name.trim().length > 0 ? name : identifier;
  const parts = source.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]![0] ?? '?').toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase();
}

/** Human-friendly time divider like "Tue, Apr 15 · 11:22 AM". */
function dividerLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay =
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return time;
  const date = d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return `${date} · ${time}`;
}

export function ThreadView({
  messages,
  tones = {},
  attachmentsByMessageId = {},
  onMarkProof,
  proofAttachmentIds,
  markProofPending,
}: ThreadViewProps) {
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const prevCountRef = useRef<number>(0);
  const lastIdRef = useRef<string | null>(null);
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1]!.id : null;

  // Polish #12 — expansion state. When the conversation switches we reset
  // back to the collapsed default. A single `expandAll` flag plus a set of
  // individually-expanded message ids keeps the logic simple.
  const [expandAll, setExpandAll] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const convoKeyRef = useRef<string | null>(null);
  // MessageRow doesn't carry conversation_id, so use the first message's id
  // as a stable per-conversation sentinel. When the user switches threads
  // the first message's id changes and we reset expansion state.
  const convoKey = messages.length > 0 ? messages[0]!.id : null;
  useEffect(() => {
    if (convoKeyRef.current !== convoKey) {
      convoKeyRef.current = convoKey;
      setExpandAll(false);
      setExpandedIds(new Set());
    }
  }, [convoKey]);

  // Auto-scroll: jump on mount / conversation switch, smooth on new message.
  useEffect(() => {
    const el = bottomRef.current;
    if (!el) return;
    const prevCount = prevCountRef.current;
    const conversationChanged =
      lastIdRef.current !== null &&
      lastIdRef.current !== lastMessageId &&
      prevCount > 0 &&
      messages.length > 0 &&
      !messages.some((m) => m.id === lastIdRef.current);
    const isFirstLoad = prevCount === 0;
    const newMessagesAppended = !isFirstLoad && !conversationChanged && messages.length > prevCount;

    if (isFirstLoad || conversationChanged) {
      el.scrollIntoView({ behavior: 'auto', block: 'end' });
    } else if (newMessagesAppended) {
      el.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }

    prevCountRef.current = messages.length;
    lastIdRef.current = lastMessageId;
  }, [messages, lastMessageId]);

  if (messages.length === 0) {
    return <div className="thread-empty">No messages in this thread yet.</div>;
  }

  const SAME_BLOCK_MS = 60 * 1000; // 60 seconds — merges rapid-fire sender messages
  const DIVIDER_MS = 10 * 60 * 1000; // 10 minutes — inserts a time divider

  // Polish #12 — decide which messages should render as full bubbles vs
  // collapsed single-line rows. The last message is always a full bubble.
  // Earlier messages render as bubbles only when the user has explicitly
  // expanded them (all at once or individually).
  const lastIdx = messages.length - 1;
  const earlierCount = messages.length > 1 ? lastIdx : 0;

  const isExpanded = (m: MessageRow, idx: number): boolean => {
    if (idx === lastIdx) return true; // latest always shown in full
    if (expandAll) return true;
    return expandedIds.has(m.id);
  };

  const expandOne = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  // The "Show N earlier messages" pill only shows when we're in the default
  // collapsed state AND there are older messages to expand. It disappears
  // once the user clicks it or expands every earlier message individually.
  const allEarlierExpanded =
    earlierCount > 0 &&
    (expandAll ||
      messages.slice(0, lastIdx).every((m) => expandedIds.has(m.id)));
  const showEarlierPill = earlierCount > 0 && !allEarlierExpanded;

  return (
    <div className="thread-list">
      {showEarlierPill ? (
        <div className="thread-show-earlier-wrap">
          <button
            type="button"
            className="thread-show-earlier"
            onClick={() => setExpandAll(true)}
            title="Expand every earlier message in this thread"
          >
            Show {earlierCount} earlier {earlierCount === 1 ? 'message' : 'messages'}
          </button>
        </div>
      ) : null}
      {messages.map((m, i) => {
        const prev = i > 0 ? messages[i - 1]! : null;
        const prevTime = prev ? new Date(prev.sent_at).getTime() : 0;
        const thisTime = new Date(m.sent_at).getTime();
        const gap = prev ? thisTime - prevTime : 0;

        // Time divider logic only applies when the adjacent messages are both
        // rendered as full bubbles — otherwise collapsed rows carry their own
        // timestamp and a separate divider would look noisy.
        const expandedHere = isExpanded(m, i);
        const prevExpanded = prev ? isExpanded(prev, i - 1) : false;
        const showDivider = expandedHere && (!prev || (prevExpanded && gap > DIVIDER_MS));

        const sameBlock =
          prev &&
          prevExpanded &&
          expandedHere &&
          prev.direction === m.direction &&
          prev.sender_identifier === m.sender_identifier &&
          gap < SAME_BLOCK_MS;

        const senderInitials = initialsFrom(m.sender_name, m.sender_identifier);

        return (
          <div key={m.id}>
            {showDivider ? (
              <div className="thread-divider" role="separator">
                <span>{dividerLabel(m.sent_at)}</span>
              </div>
            ) : null}
            {expandedHere ? (
              <MessageBubble
                direction={m.direction}
                senderName={m.sender_name}
                senderInitials={senderInitials}
                body={m.body}
                bodyRaw={m.body_raw}
                sentAt={m.sent_at}
                hideHeader={!!sameBlock}
                attachments={attachmentsByMessageId[m.id] ?? null}
                tone={tones[m.id] ?? null}
                onMarkProof={m.direction === 'outbound' ? onMarkProof : undefined}
                proofAttachmentIds={
                  m.direction === 'outbound' ? proofAttachmentIds : undefined
                }
                markProofPending={
                  m.direction === 'outbound' ? markProofPending : undefined
                }
              />
            ) : (
              <CollapsedMessageRow
                direction={m.direction}
                senderName={m.sender_name}
                senderInitials={senderInitials}
                body={m.body}
                sentAt={m.sent_at}
                onExpand={() => expandOne(m.id)}
              />
            )}
          </div>
        );
      })}
      <div ref={bottomRef} aria-hidden />
    </div>
  );
}
